import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useFusionAuth } from '@fusion/auth-v3'
import {
  getWidgetSelectedDevices,
  getWidgetAvailableMeasurements,
  getSelectedMeasurement,
  getDevicesPermissionSet,
  type WidgetDevice
} from '@/helpers/widgets/helper'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import type { MeasurementType } from '@/services/device-measurements'
import type { WidgetSettings } from '@/types/dashboard'
import { NAMESPACES, type RootState } from '@/store'
import { DEVICE_GETTERS } from '@/store/devices'

/** Reactive props shape consumed by `useMeasurementOptions`.
 *  `accountId` is resolved internally — do not include it here.
 *  Pass the component's reactive `props` directly when all needed fields
 *  are props, or a `reactive({...})` merged object when some fields
 *  (e.g. `deviceIds`, `defaultParameter`) live in data or computed. */
export interface MeasurementOptionsProps {
  deviceIds: string[]
  passedinParameter?: number | null
  filterMeasurementStatistics?: boolean
  defaultParameter?: number | null
  settings?: WidgetSettings | null
}

export function useMeasurementOptions(props: MeasurementOptionsProps) {
  const store = useStore<RootState>()
  const auth = useFusionAuth()

  // Reactive to loginas store changes via getLoggedInAsUser() inside getUserAccountId
  const accountId = computed<string>(() => getUserAccountId(auth))

  const selectedParameter = ref<number | null>(props.passedinParameter ?? null)

  const selectedDevices = computed<WidgetDevice[]>(() =>
    getWidgetSelectedDevices(props.deviceIds, accountId.value)
  )

  const permissionSet = computed<string[]>(() => getDevicesPermissionSet(selectedDevices.value))

  function isAConnectedDevice(deviceId: string): boolean {
    return !!store.getters[
      `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID}`
    ](deviceId)
  }

  const filterMeasurements = computed<boolean>(() => {
    if (props.deviceIds?.length) {
      for (const deviceId of props.deviceIds) {
        if (isAConnectedDevice(deviceId)) {
          return false
        }
      }
    }
    return true
  })

  const availableMeasurements = computed<MeasurementType[]>(() =>
    getWidgetAvailableMeasurements(
      selectedDevices.value,
      filterMeasurements.value,
      props.filterMeasurementStatistics ?? false
    )
  )

  function checkSelectedParameter(): void {
    if (availableMeasurements.value?.length) {
      selectedParameter.value = getSelectedMeasurement(
        selectedParameter.value,
        availableMeasurements.value,
        {
          defaultParameter: props.defaultParameter ?? undefined,
          savedParameter: props.settings?.selectedParameter
        }
      )
    }
  }

  watch(availableMeasurements, () => checkSelectedParameter(), { deep: true })

  watch(
    () => props.passedinParameter,
    (newValue) => {
      selectedParameter.value = newValue ?? null
      checkSelectedParameter()
    }
  )

  // Run once immediately so selectedParameter is resolved even when
  // availableMeasurements is already populated at setup time (e.g. re-opening a dialog).
  checkSelectedParameter()

  return {
    accountId,
    selectedParameter,
    filterMeasurements,
    availableMeasurements,
    selectedDevices,
    permissionSet,
    checkSelectedParameter,
    isAConnectedDevice
  }
}
